import BlockContent from '@sanity/block-content-to-react';
import {Image} from '@shopify/hydrogen';
import clsx from 'clsx';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';

import type {PortableTextBlock} from '@sanity/types';

import LinkEmailAnnotation from './annotations/LinkEmail';
import LinkPhoneAnnotation from './annotations/LinkPhone';
import LinkExternalAnnotation from './annotations/LinkExternal';
import LinkInternalAnnotation from './annotations/LinkInternal';
import LinkAnchorAnnotation from './annotations/LinkAnchor';
import Block from './blocks/Block';
import ListBlock from './blocks/List';

import {isMobile} from '../ui/animationVars';
import Table from './blocks/Table';
import {ModalForm} from './blocks/ModalForm';
import EmbedForm from './annotations/EmbedForm';
import FluidVideo from '../media/fluidVideo.client';

type Props = {
  blocks: PortableTextBlock[];
  className?: string;
  animate?: boolean;
};

const textVars = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export default function PortableText({blocks, className, animate}: Props) {
  const {ref, inView} = useInView({
    threshold: 0.7,
    triggerOnce: true,
  });

  const contentHasVideo = blocks.some((block) => block._type === 'video');

  return (
    <motion.div
      ref={contentHasVideo ? null : ref}
      variants={textVars}
      animate={animate && !isMobile && (inView ? 'animate' : 'initial')}
      className={clsx('portableText', className)}
    >
      <BlockContent
        blocks={blocks}
        className={clsx('flex flex-col gap-4 md:gap-5')}
        renderContainerOnSingleChild
        serializers={{
          // Lists
          list: ListBlock,
          // Marks
          marks: {
            embedForm: EmbedForm,
            annotationLinkEmail: LinkEmailAnnotation,
            annotationLinkPhone: LinkPhoneAnnotation,
            annotationLinkExternal: LinkExternalAnnotation,
            annotationLinkInternal: LinkInternalAnnotation,
            annotationLinkAnchor: LinkAnchorAnnotation,
            highlight: (props: any) => (
              <span className="!text-orange">{props.children}</span>
            ),
            underline: (props: any) => (
              <span className="!underline">{props.children}</span>
            ),
          },
          // Block types
          types: {
            block: Block,
            hr: (props: any) => (
              <div
                style={{backgroundColor: props?.node?.color || 'red'}}
                className="h-[3px] w-1/3"
              />
            ),
            table: (props: any) => <Table {...props?.node} />,
            modalForm: (props: any) => <ModalForm {...props?.node} />,
            image: (props: any) => (
              <Image
                alt={'inline image'}
                data={props?.node}
                width="100%"
                height="100%"
                className="w-full py-space-s"
              />
            ),
            video: (props: any) => (
              <div className="relative h-full w-full py-space-s">
                <FluidVideo
                  controls
                  url={props?.node?.url}
                  className="w-full"
                />
              </div>
            ),
          },
        }}
      />
    </motion.div>
  );
}
